export const setScrollTopProgrammatically = ({
  element,
  value,
  suppressScrollRef,
  anchorScrollAnimationRef,
  smooth = false,
  duration,
}) => {
  if (!element) return;
  const existingAnimation = anchorScrollAnimationRef.current;
  if (existingAnimation?.rafId) {
    cancelAnimationFrame(existingAnimation.rafId);
    if (existingAnimation.suppressed) {
      suppressScrollRef.current = Math.max(0, suppressScrollRef.current - 1);
    }
    anchorScrollAnimationRef.current = null;
  }

  const startTop = element.scrollTop;
  const targetTop = Math.max(0, value);
  const distance = targetTop - startTop;
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!smooth || prefersReducedMotion || Math.abs(distance) < 4) {
    suppressScrollRef.current++;
    element.scrollTop = targetTop;
    window.setTimeout(() => {
      suppressScrollRef.current = Math.max(0, suppressScrollRef.current - 1);
    }, 120);
    return;
  }

  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  suppressScrollRef.current++;
  const animation = {
    rafId: null,
    suppressed: true,
  };
  anchorScrollAnimationRef.current = animation;

  const step = (now) => {
    if (anchorScrollAnimationRef.current !== animation) return;
    const elapsed = Math.max(0, now - startedAt);
    const progress = Math.min(1, elapsed / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    element.scrollTop = startTop + distance * eased;

    if (progress < 1) {
      animation.rafId = requestAnimationFrame(step);
      return;
    }

    element.scrollTop = targetTop;
    anchorScrollAnimationRef.current = null;
    window.setTimeout(() => {
      if (animation.suppressed) {
        animation.suppressed = false;
        suppressScrollRef.current = Math.max(0, suppressScrollRef.current - 1);
      }
    }, 40);
  };

  animation.rafId = requestAnimationFrame(step);
};

export const clearAnchorPad = ({
  pad,
  scroller,
  preserveViewport = false,
}) => {
  if (!pad) return;

  if (preserveViewport) {
    const padHeight = pad.offsetHeight || 0;
    if (scroller && padHeight > 0) {
      const scrollHeightWithoutPad = Math.max(0, scroller.scrollHeight - padHeight);
      const maxTopWithoutPad = Math.max(0, scrollHeightWithoutPad - scroller.clientHeight);
      const requiredPad = Math.ceil(scroller.scrollTop - maxTopWithoutPad);

      if (requiredPad > 1) {
        pad.style.height = `${requiredPad}px`;
        return;
      }
    }
  }

  pad.style.height = "0px";
};

export const captureViewportAnchor = ({
  scroller,
  messageRefs,
  excludeIds = [],
}) => {
  if (!scroller) return null;
  const scrollerRect = scroller.getBoundingClientRect();
  const visibleTop = scrollerRect.top + 1;
  const visibleBottom = scrollerRect.bottom - 1;
  const excluded = new Set(excludeIds.filter(Boolean));

  for (const [id, node] of messageRefs.entries()) {
    if (excluded.has(id)) continue;
    if (!node?.getBoundingClientRect) continue;
    const rect = node.getBoundingClientRect();
    if (rect.bottom <= visibleTop || rect.top >= visibleBottom) continue;
    return {
      id,
      top: rect.top - scrollerRect.top,
    };
  }

  return null;
};

export const restoreViewportAnchor = ({
  anchor,
  scroller,
  messageRefs,
  suppressScrollRef,
}) => {
  if (!anchor?.id) return;
  const node = messageRefs.get(anchor.id);
  if (!scroller || !node?.getBoundingClientRect) return;

  const scrollerRect = scroller.getBoundingClientRect();
  const rect = node.getBoundingClientRect();
  const currentTop = rect.top - scrollerRect.top;
  const delta = currentTop - anchor.top;
  if (!Number.isFinite(delta) || Math.abs(delta) < 0.5) return;

  suppressScrollRef.current++;
  scroller.scrollTop = Math.max(0, scroller.scrollTop + delta);
  suppressScrollRef.current--;
};

export const waitForImages = async (root, maxWaitMs) => {
  if (!root) return;
  const images = Array.from(root.querySelectorAll("img"));
  if (!images.length) return;
  await Promise.race([
    Promise.allSettled(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              const done = () => {
                image.removeEventListener("load", done);
                image.removeEventListener("error", done);
                resolve();
              };
              image.addEventListener("load", done);
              image.addEventListener("error", done);
            })
      )
    ),
    new Promise((resolve) => setTimeout(resolve, maxWaitMs)),
  ]);
};

export const waitForStableHeight = async (
  element,
  { stableFrames = 2, maxFrames } = {}
) => {
  let last = -1;
  let stable = 0;
  let frames = 0;
  while (stable < stableFrames && frames < maxFrames) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const height = element.scrollHeight;
    if (Math.abs(height - last) < 1) {
      stable += 1;
    } else {
      stable = 0;
    }
    last = height;
    frames += 1;
  }
};
